/* eslint-disable max-len */
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../Legacy';
import { Popover, PopoverProps } from '../app/canvas/ui/Popover';
import { Icon } from '../concepts/resources/Icon';
import { Colors } from './Colors';
import { Skeleton } from './Skeleton';
import { toRem } from './Utils';

const checkIcon = 'data:image/svg+xml,%3Csvg width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M5 13L9 17L19 7\' fill=\'none\' stroke=\'%232141ab\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E%0A';

const Badge = styled.div`
    display: inline-block;
    margin-left: auto;
    padding: .125rem .25rem;
    border-radius: .25rem;
    font-size: .75rem;
    font-weight: 450;
    color: ${Colors.text};
    background: ${Colors.l1};
`;

const Hint = styled.div`
    display: inline-block;
    margin-left: .25rem;
    padding: .125rem .25rem;
    border-radius: .25rem;
    font-size: .75rem;
    font-weight: 450;
    color: ${Colors.text};
    background: ${Colors.l1};
`;

const HintStrip = styled.div`
    margin-left: auto;
    padding-left: 1rem;
    margin-top: -0.25rem;
    white-space: nowrap;
`;

const Shortcut = React.memo<{ keys: string }>(({ keys }) => {
    const parts = (keys || '').split('+').map(x => x.trim()).filter(x => !!x);
    return <HintStrip>
        {parts.map(x => <Hint key={x}>{x}</Hint>)}
    </HintStrip>;
});

const ItemText = styled.span`
    margin-right: 1.5rem;
    white-space: nowrap;
`;

const ItemTextCropped = styled.span<{ maxWidth?: any }>`
    display: inline-block;
    margin-right: .5rem;
    max-width: ${p => toRem(p.maxWidth || 20)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemPush = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
    padding-left: 1rem;
`;

const Divide = styled.div`
    background: ${Colors.gray200};
    height: 1px;
    margin: .25rem 0 !important;
`;

const Title = styled.div`
    padding: .25rem 1rem;
    color: ${Colors.textLight};
    font-size: 0.8125rem;
    font-weight: 450;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Border = styled.div`
    padding: .25rem 0;

    & * {
        user-select: none;
    }
`;

type ItemProps = { highlight?: boolean, disabled?: boolean, checked?: boolean };
const Item = styled.div<ItemProps>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 auto;
    position: relative;
    padding: .25rem .5rem;
    text-align: left;
    justify-content: flex-start;
    font-weight: 400; 
    color: ${Colors.text};
    cursor: pointer;

    &:hover {
        background: ${Colors.hover}
    }

    &:active {
        background: ${Colors.active}
    }
    
    ${p => p.disabled && css`
        pointer-events: none;
        color: gainsboro;
    `}

    ${p => p.highlight && css`
        background: ${Colors.hover} !important;
    `}

    ${p => p.checked && css`
        &:after {
            content: '';
            position: absolute;
            top: 50%;
            right: .75rem;
            transform: translateY(-0.625rem);
            width: 1.25rem;
            height: 1.25rem;
            background-size: contain;
            background-image: url("${checkIcon}");
        }
    `}

    ${ItemText}:first-child {
        margin-left: 0.5rem;
    }

    ${ItemTextCropped}:first-child {
        margin-left: 0.5rem;
    }

    ${Skeleton} {
        height: 1.25rem;
        flex: 1 1 auto;
        margin: .125rem 0rem !important;
    }
`;

const Section: LFC<{ title?: string, first?: boolean }> = ({ title, first, children }) => <>
    {!first && <Divide />}
    {!!title && <Title>{title}</Title>}
    {children}
</>;

const Base: LFC<{ w?: string | number, mw?: string | number }> = ({ w, mw, children }) => {
    const styling = {} as any;
    if (w) styling.width = toRem(w);
    if (mw) styling.minWidth = toRem(mw);
    return <Border role="menu">
        {children}
    </Border>;
};

export type SubMenuItemProps = ItemProps & Pick<PopoverProps, 'placement' | 'inner'> & {
    children: React.ReactNode,
    menu?: React.ComponentType<{ onClose?(): void }>,
    content?: React.ReactNode,
};

export const SubMenuItem = React.memo(({ menu, content, children, ...props }: SubMenuItemProps) => {
    const { placement, inner, ...itemProps } = props;

    const [open, setOpen] = useState(false);

    const SubMenu = menu;
    content = content || <SubMenu onClose={() => setOpen(false)} />;

    return <Popover
        block
        open={open}
        onOpenChange={setOpen}
        placement={placement || 'right-start'}
        inner={inner || { direction: 'right', offset: '.5' }}
        portal
        content={content}
    >
        <Item {...itemProps}>
            {children}
        </Item>
    </Popover>;

});

const SubMenuIndicatorFrame = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
    padding-left: 1rem;
`;

const SubMenuIndicator = React.memo(() =>
    <SubMenuIndicatorFrame>
        <Icon size="1" src="deqo://icons/hero/chevron-right" />
    </SubMenuIndicatorFrame>
);

export const Menu = Object.assign(Base, {
    Divide,
    Item,
    ItemText,
    ItemTextCropped,
    ItemPush,
    Badge,
    Section,
    SectionTitle: Title,
    Shortcut,
    SubMenuItem,
    SubMenuIndicator,
});
